/**
 * 영상 제작 — 운영자 화면(콘텐츠 › 영상 제작) (261103-video-production-plan).
 *
 *  - 결제 기간마다 업체당 `VIDEOS_PER_CYCLE` 편. 「영상 업로드 계획 체크」가 부족분만큼 주제·업로드 시각을 정해 계획을 만든다
 *  - 생산은 PC(video 앱)가 계획을 가져가서 한다 — 그 경로는 `../video.ts`(`access: 'video'`)
 *  - 대상 업체는 기사 작성과 같다 — 이용 중 브랜드 + 지금 생산 가능한 구독
 */
import { z } from 'zod';
import { IdParams, IsoDateTime, KstDate, Ok, Uuid } from '../common.ts';
import { defineRoute } from '../route.ts';

/** 결제 기간마다 업체당 영상 수 — 계약서 「구독 기간마다 2편」 */
export const VIDEOS_PER_CYCLE = 2;
/** 업로드 칸(KST 시) — 모든 업체가 같은 채널에 올라가 한 칸에 한 편이다 */
export const VIDEO_SLOT_HOURS = [8, 20] as const;
export const VIDEO_PLAN_STATUSES = ['planned', 'claimed', 'scheduled', 'canceled', 'failed'] as const;
export const VideoPlanStatus = z.enum(VIDEO_PLAN_STATUSES);
export type VideoPlanStatus = z.infer<typeof VideoPlanStatus>;
export const MAX_SEARCH_PHRASES = 3;

/** 같은 주제 판정 키 — 공백·문장부호를 빼고 소문자. 서비스와 화면이 같은 식을 쓴다 */
export function topicKey(topic: string): string {
  return topic
    .normalize('NFC')
    .toLowerCase()
    .replace(/[\s.,!?·…'"“”‘’~\-_()[\]]/g, '');
}

const Text = (max: number) => z.string().trim().min(1).max(max);
export const VideoTopic = z
  .string()
  .trim()
  .transform((s) => s.replace(/\s+/g, ' '))
  .pipe(z.string().min(4).max(120));

export const VideoProfileInput = z
  .object({
    businessName: Text(60),
    category: Text(40),
    searchPhrases: z.array(Text(80)).min(1).max(MAX_SEARCH_PHRASES),
  })
  .strict();
export type VideoProfileInput = z.infer<typeof VideoProfileInput>;

export const VideoProfile = z.object({
  brandId: Uuid,
  businessName: z.string(),
  category: z.string(),
  searchPhrases: z.array(z.string()),
  updatedAt: IsoDateTime,
});
export type VideoProfile = z.infer<typeof VideoProfile>;

export const VideoBoardRow = z.object({
  brandId: Uuid,
  brandName: z.string(),
  cycle: z.object({ from: KstDate, to: KstDate, daysLeft: z.number().int() }),
  /** 이번 결제 기간: 만든 영상(예약 포함) · 아직 영상이 안 된 계획 */
  videos: z.number().int(),
  plans: z.number().int(),
  need: z.number().int(),
  profile: VideoProfile.nullable(),
  /** 지금까지 쓴 주제 수(가져온 이력 + 계획) */
  usedTopics: z.number().int(),
});
export type VideoBoardRow = z.infer<typeof VideoBoardRow>;

/** 구독은 있으나 지금 생산할 수 없는 업체 — 계획을 만들지 않는다. 이유를 그대로 보인다 */
export const VideoBlockedRow = z.object({ brandId: Uuid, brandName: z.string(), reason: z.string() });
export type VideoBlockedRow = z.infer<typeof VideoBlockedRow>;

export const VideoPlanRow = z.object({
  id: Uuid,
  brandId: Uuid,
  brandName: z.string(),
  cycleFrom: KstDate,
  cycleTo: KstDate,
  slotAt: IsoDateTime,
  gapException: z.boolean(),
  topic: z.string(),
  businessName: z.string(),
  category: z.string(),
  searchPhrases: z.array(z.string()),
  status: VideoPlanStatus,
  claimedAt: IsoDateTime.nullable(),
  scheduledAt: IsoDateTime.nullable(),
  externalKey: z.string().nullable(),
  error: z.string().nullable(),
});
export type VideoPlanRow = z.infer<typeof VideoPlanRow>;

export const VideoBoard = z.object({
  now: IsoDateTime,
  topicWriterReady: z.boolean(),
  rows: z.array(VideoBoardRow),
  blocked: z.array(VideoBlockedRow),
  /** 열린 계획 전부 + 최근 30일의 끝난 계획 — 업로드 시각 순 */
  plans: z.array(VideoPlanRow),
});
export type VideoBoard = z.infer<typeof VideoBoard>;

export const VideoCheckResult = z.object({
  created: z.number().int(),
  gapExceptions: z.number().int(),
  /** 계획을 못 만든 업체와 이유(정보 필요·주제 생성 실패·기간 안 칸 없음) */
  skipped: z.array(z.object({ brandId: Uuid, brandName: z.string(), reason: z.string() })),
});
export type VideoCheckResult = z.infer<typeof VideoCheckResult>;

export const getVideoBoard = defineRoute({
  method: 'get',
  path: '/api/videos/board',
  access: 'admin',
  summary: '업체별 이번 결제 기간의 영상 수·부족분, 생산 불가 업체, 계획 목록',
  response: VideoBoard,
});

export const checkVideoPlan = defineRoute({
  method: 'post',
  path: '/api/videos/plan/check',
  access: 'admin',
  summary: '영상 업로드 계획 체크 — 부족분만큼 주제·업로드 시각을 정해 계획을 만든다(여러 번 눌러도 부족분만)',
  response: VideoCheckResult,
});

export const updateVideoPlan = defineRoute({
  method: 'patch',
  path: '/api/videos/plans/{id}',
  access: 'admin',
  summary: '계획의 주제를 고친다 — PC 가 가져가기 전(planned)만. 이미 쓴 주제면 409',
  params: IdParams,
  body: z.object({ topic: VideoTopic }).strict(),
  response: VideoPlanRow,
});

export const regenerateVideoPlanTopic = defineRoute({
  method: 'post',
  path: '/api/videos/plans/{id}/regenerate-topic',
  access: 'admin',
  summary: '계획의 주제를 새로 만든다 — planned 만',
  params: IdParams,
  response: VideoPlanRow,
});

export const cancelVideoPlan = defineRoute({
  method: 'post',
  path: '/api/videos/plans/{id}/cancel',
  access: 'admin',
  summary: '계획 취소 — planned 만. 칸을 놓고, 그 계획의 주제를 이력에서 뺀다',
  params: IdParams,
  response: Ok,
});

export const getVideoProfile = defineRoute({
  method: 'get',
  path: '/api/videos/profiles/{id}',
  access: 'admin',
  summary: '업체의 영상 정보와 이미 쓴 주제',
  params: IdParams,
  response: z.object({
    profile: VideoProfile.nullable(),
    topics: z.array(z.object({ topic: z.string(), source: z.enum(['import', 'plan']), createdAt: IsoDateTime })),
  }),
});

export const putVideoProfile = defineRoute({
  method: 'put',
  path: '/api/videos/profiles/{id}',
  access: 'admin',
  summary: '업체의 영상 정보(업체명·카테고리·검색문장) 저장',
  params: IdParams,
  body: VideoProfileInput,
  response: VideoProfile,
});

/** 가져오기 한 묶음 — 화면이 CSV 를 읽어 업체별로 묶어 보낸다(`web/src/lib/video-csv.ts`) */
export const VideoImportItem = z
  .object({
    brandId: Uuid,
    /** null 이면 프로필을 건드리지 않고 주제 이력만 더한다 */
    profile: VideoProfileInput.nullable(),
    topics: z.array(VideoTopic).max(200),
  })
  .strict();
export type VideoImportItem = z.infer<typeof VideoImportItem>;

export const importVideoData = defineRoute({
  method: 'post',
  path: '/api/videos/import',
  access: 'admin',
  summary: '기존 CSV 의 업체 정보·사용한 주제를 넣는다 — 프로필은 덮어쓰고 주제는 더한다. 없는 업체는 건너뛴다',
  body: z.object({ items: z.array(VideoImportItem).min(1).max(300) }).strict(),
  response: z.object({
    profiles: z.number().int(),
    topics: z.number().int(),
    skipped: z.array(z.object({ brandId: Uuid, reason: z.string() })),
  }),
});
